import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    TextField,
    Typography,
    Chip,
    Button,
    Stack,
    Paper,
    Divider,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Grid from "@mui/material/Grid";
import { Permission, Role } from "../../../types/userTypes";
import { OP_BUTTON_STYLE } from "../../../constants/styles";
import CancelIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

interface RoleFormProps {
    initialRole?: Role | null; // Se presente siamo in Update, altrimenti Create
    availablePermissions: Permission[];
    onSave: (roleData: Omit<Role, 'id'> & { id?: string }) => void;
    onCancel?: () => void;
}

// Mappatura dell'ordinamento fisso per le operazioni
const OPERATION_ORDER: Record<string, number> = {
    'read': 1,
    'create': 2,
    'update': 3,
    'delete': 4
};

// Funzione helper robusta per formattare le stringhe (rimozione underscore e Title Case)
const formatString = (str: string | null | undefined): string => {
    if (!str || typeof str !== 'string') return '';
    return str
        .replace(/_/g, ' ')
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

export const RoleForm: React.FC<RoleFormProps> = ({
                                                      initialRole,
                                                      availablePermissions = [],
                                                      onSave,
                                                      onCancel
                                                  }) => {
    // Form states
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPermissionIds, setSelectedPermissionIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (initialRole) {
            setName(initialRole.name || '');
            setDescription(initialRole.description || '');
            const initialIds = initialRole.permissions?.map((p) => p?.id).filter(Boolean) || [];
            setSelectedPermissionIds(new Set(initialIds));
        } else {
            setName('');
            setDescription('');
            setSelectedPermissionIds(new Set());
        }
    }, [initialRole]);

    // Toggle selezione singolo permesso
    const handleTogglePermission = (permissionId: string) => {
        if (!permissionId) return;
        setSelectedPermissionIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(permissionId)) {
                newSet.delete(permissionId);
            } else {
                newSet.add(permissionId);
            }
            return newSet;
        });
    };

    // Seleziona tutti i permessi attualmente disponibili
    const handleSelectAll = () => {
        const allIds = availablePermissions.map((p) => p?.id).filter(Boolean);
        setSelectedPermissionIds(new Set(allIds));
    };

    // Deseleziona tutti i permessi
    const handleDeselectAll = () => {
        setSelectedPermissionIds(new Set());
    };

    // Logica di filtraggio e raggruppamento (Entity -> Operation -> Permissions)
    const groupedPermissions = useMemo(() => {
        const groups: Record<string, Record<string, Permission[]>> = {};

        if (!Array.isArray(availablePermissions)) return groups;

        availablePermissions.forEach((permission) => {
            if (!permission) return;

            const permName = permission.name || '';

            if (
                searchTerm &&
                !permName.toLowerCase().includes(searchTerm.toLowerCase())
            ) {
                return;
            }

            // Fallback sicuri nel caso in cui entità o operazione siano null/undefined/empty
            const entity = permission.entity?.trim() || 'UNKNOWN_ENTITY';
            const operation = permission.operation?.trim() || 'UNKNOWN_OPERATION';

            if (!groups[entity]) {
                groups[entity] = {};
            }
            if (!groups[entity][operation]) {
                groups[entity][operation] = [];
            }

            groups[entity][operation].push(permission);
        });

        return groups;
    }, [availablePermissions, searchTerm]);

    // Invio del form
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;

        const finalPermissions = availablePermissions.filter((p) =>
            p && selectedPermissionIds.has(p.id)
        );

        onSave({
            id: initialRole?.id,
            name: name.trim(),
            description: description.trim(),
            permissions: finalPermissions
        });
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mx: 'auto', p: 2 }}>
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                    {initialRole ? 'Edit Role' : 'New Role'}
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack spacing={3}>
                    {/* Nome Ruolo */}
                    <TextField
                        label="Role Name"
                        variant="outlined"
                        fullWidth
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />

                    {/* Descrizione */}
                    <TextField
                        label="Description (Optional)"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </Stack>
            </Paper>

            {/* Sezione Permessi */}
            <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="semiBold">
                            Associated Permissions
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                Selected: {selectedPermissionIds.size}
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Barra di Ricerca e Azioni */}
                    <Box sx={{ ml: 'auto' }} display={'flex'} gap={2}>
                        <Button
                            variant="contained"
                            onClick={handleSelectAll}
                            sx={OP_BUTTON_STYLE}
                        >
                            Select All
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleDeselectAll}
                            sx={OP_BUTTON_STYLE}
                        >
                            Deselect All
                        </Button>
                        <TextField
                            size="small"
                            placeholder="Search permission..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{ minWidth: 250 }}
                        />
                    </Box>
                </Stack>

                <Divider sx={{ mb: 3 }} />

                {Object.keys(groupedPermissions).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 4 }}>
                        No permissions found matching the search criteria.
                    </Typography>
                ) : (
                    Object.entries(groupedPermissions)
                        .sort(([entityA], [entityB]) => entityA.localeCompare(entityB)) // Ordine alfabetico per le entità
                        .map(([entity, operations]) => (
                            <Box key={entity} sx={{ mb: 4 }}>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1.5 }}>
                                    {formatString(entity)}
                                </Typography>

                                <Grid container spacing={3}>
                                    {Object.entries(operations)
                                        .sort(([opA], [opB]) => {
                                            const orderA = OPERATION_ORDER[opA.toLowerCase()] || 99;
                                            const orderB = OPERATION_ORDER[opB.toLowerCase()] || 99;
                                            return orderA - orderB;
                                        })
                                        .map(([operation, permissions]) => (
                                            <Grid size={{ xs: 12, md: 6, lg: 3 }} key={operation}>
                                                {/* Nome Operazione formattato */}
                                                <Typography variant="body2" fontWeight="medium" color="text.secondary" sx={{ mb: 1 }}>
                                                    {formatString(operation)}
                                                </Typography>

                                                {/* Contenitore dei Chip */}
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {permissions.map((permission) => {
                                                        if (!permission) return null;
                                                        const isSelected = selectedPermissionIds.has(permission.id);
                                                        return (
                                                            <Chip
                                                                key={permission.id}
                                                                label={formatString(permission.name)}
                                                                onClick={() => handleTogglePermission(permission.id)}
                                                                color={isSelected ? "primary" : "default"}
                                                                variant={isSelected ? "filled" : "outlined"}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    fontWeight: isSelected ? 'bold' : 'normal',
                                                                    '&:hover': {
                                                                        backgroundColor: isSelected ? 'primary.dark' : 'action.hover',
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            </Grid>
                                        ))}
                                </Grid>

                                <Divider sx={{ mt: 3, opacity: 0.5 }} />
                            </Box>
                        ))
                )}

                {/* Pulsanti di Azione del Form */}
                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={!name.trim()}
                        sx={OP_BUTTON_STYLE}
                    >
                        {initialRole ? 'Save' : 'Create'}
                    </Button>
                    {onCancel && (
                        <Button sx={OP_BUTTON_STYLE} variant="contained" startIcon={<CancelIcon />} onClick={onCancel}>
                            Cancel
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};